import { Component, computed, inject, signal } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { QrRepairTaskDto, RepairTaskQrService } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { openRepairTaskCloseDialog, RepairTaskCloseMode } from '@components/dialog/repair-task-close-dialog/repair-task-close-dialog.component';
import { qrBuildingContext } from '@services/qr-building-context.service';

const FinisherNameKey = 'wr-qr-finisher-name';
const FinisherNameMaxLength = 100;

@Component({
  standalone: false,
  selector: 'wr-qr-code-repair-task-page',
  templateUrl: './qr-code-repair-task-page.component.html',
  styleUrls: ['./qr-code-repair-task-page.component.scss'],
})
export class QrCodeRepairTaskPageComponent {
  private readonly repairTaskQrService = inject(RepairTaskQrService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  readonly context = qrBuildingContext();
  readonly loading = signal(false);
  readonly onlyThisSection = signal(false);
  readonly finisherName = signal(readFinisherName());
  readonly finisherNameMaxLength = FinisherNameMaxLength;

  readonly repairTasks = asyncComputed(() => {
    const buildingId = this.context.buildingId();
    if (!buildingId || !this.context.session()?.canViewRepairTasks) {
      return undefined;
    }
    return this.repairTaskQrService.findAllOpenByBuildingId(buildingId);
  }, undefined);

  readonly visibleRepairTasks = computed(() => {
    const tasks = this.repairTasks() ?? [];
    return this.onlyThisSection() ? tasks.filter(task => this.inSelectedSection(task)) : tasks;
  });

  readonly sectionTaskCount = computed(() => (this.repairTasks() ?? []).filter(task => this.inSelectedSection(task)).length);

  inSelectedSection(task: QrRepairTaskDto): boolean {
    return !!task.buildingSectionId && task.buildingSectionId === this.context.sectionId();
  }

  updateFinisherName(name: string): void {
    this.finisherName.set(name);
    storeFinisherNameIfPossible(name);
  }

  async close(task: QrRepairTaskDto, mode: RepairTaskCloseMode): Promise<void> {
    const external = this.context.isExternal();
    const result = await openRepairTaskCloseDialog(this.dialogService, mode, task.title, !external);
    if (!result) {
      return;
    }
    const finisherName = external ? undefined : this.finisherName().trim() || undefined;
    this.loading.set(true);
    try {
      if (mode === 'finish') {
        await firstValueFrom(this.repairTaskQrService.finish(task.id, { ...result, finisherName }));
        this.toastrService.success('Reparaturauftrag wurde abgeschlossen', 'Abgeschlossen');
      } else {
        await firstValueFrom(this.repairTaskQrService.decline(task.id, { comment: result.comment, finisherName }));
        this.toastrService.success('Reparaturauftrag wurde storniert', 'Storniert');
      }
      this.repairTasks.reload();
    } finally {
      this.loading.set(false);
    }
  }

  isOverdue(task: QrRepairTaskDto): boolean {
    return !!task.deadline && new Date(task.deadline).getTime() < Date.now();
  }
}

function readFinisherName(): string {
  try {
    return sessionStorage.getItem(FinisherNameKey) ?? '';
  } catch {
    return '';
  }
}

function storeFinisherNameIfPossible(name: string): void {
  try {
    sessionStorage.setItem(FinisherNameKey, name);
  } catch { /* no-op */ }
}
